/**
 * Catálogo beta Barrios CABA/GBA — fuente compartida (API + admin + worker + scripts).
 * CABA: 48 barrios oficiales GCBA + zonas carrier (Microcentro/Centro).
 * GBA: 24 partidos conurbano + AMBA norte/sur con localidades reales.
 */

export function normGeoKey(s: string): string {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** 48 barrios oficiales de la Ciudad Autónoma de Buenos Aires (GCBA). */
export const CABA_BETA_NEIGHBORHOODS = [
  "Agronomía",
  "Almagro",
  "Balvanera",
  "Barracas",
  "Belgrano",
  "Boedo",
  "Caballito",
  "Chacarita",
  "Coghlan",
  "Colegiales",
  "Constitución",
  "Flores",
  "Floresta",
  "La Boca",
  "La Paternal",
  "Liniers",
  "Mataderos",
  "Monte Castro",
  "Monserrat",
  "Nueva Pompeya",
  "Núñez",
  "Palermo",
  "Parque Avellaneda",
  "Parque Chacabuco",
  "Parque Chas",
  "Parque Patricios",
  "Puerto Madero",
  "Recoleta",
  "Retiro",
  "Saavedra",
  "San Cristóbal",
  "San Nicolás",
  "San Telmo",
  "Vélez Sársfield",
  "Versalles",
  "Villa Crespo",
  "Villa del Parque",
  "Villa Devoto",
  "Villa General Mitre",
  "Villa Lugano",
  "Villa Luro",
  "Villa Ortúzar",
  "Villa Pueyrredón",
  "Villa Real",
  "Villa Riachuelo",
  "Villa Santa Rita",
  "Villa Soldati",
  "Villa Urquiza",
] as const;

/** PoP mobile/fibra — no son barrios GCBA pero aparecen en CF/IPLocate (ver docs/IPLOCATE_CITY_PRECISION.md). */
export const CABA_CARRIER_NEIGHBORHOODS = ["Microcentro", "Centro"] as const;

export const CABA_BETA_NEIGHBORHOOD_SET = new Set(
  [...CABA_BETA_NEIGHBORHOODS, ...CABA_CARRIER_NEIGHBORHOODS].map((n) => normGeoKey(n)),
);

/**
 * Partidos GBA (conurbano bonaerense + AMBA) con localidades/barrios reales.
 * `partido` = ciudad en DB; `barrios` = localidades que reporta CF/IPLocate.
 */
export const GBA_BETA_CLUSTERS: ReadonlyArray<{ partido: string; barrios: readonly string[] }> = [
  // —— Norte ——
  {
    partido: "Vicente López",
    barrios: [
      "Vicente López",
      "Olivos",
      "Florida",
      "Florida Oeste",
      "Munro",
      "Villa Martelli",
      "La Lucila",
      "Carapachay",
    ],
  },
  {
    partido: "San Isidro",
    barrios: ["San Isidro", "Martínez", "Beccar", "Villa Adelina", "Boulogne", "Acassuso"],
  },
  {
    partido: "San Fernando",
    barrios: ["San Fernando", "Victoria", "Virreyes"],
  },
  {
    partido: "Tigre",
    barrios: [
      "Tigre",
      "Don Torcuato",
      "Benavídez",
      "General Pacheco",
      "El Talar",
      "Troncos del Talar",
      "Rincón de Milberg",
      "Nordelta",
    ],
  },
  {
    partido: "Escobar",
    barrios: [
      "Belén de Escobar",
      "Escobar",
      "Garín",
      "Maquinista Savio",
      "Ingeniero Maschwitz",
      "Loma Verde",
    ],
  },
  {
    partido: "Pilar",
    barrios: ["Pilar", "Del Viso", "Derqui", "Manzanares", "Fátima", "Presidente Derqui", "Villa Rosa"],
  },
  // —— Oeste ——
  {
    partido: "Morón",
    barrios: ["Morón", "Castelar", "Haedo", "El Palomar", "Villa Sarmiento"],
  },
  {
    partido: "Hurlingham",
    barrios: ["Hurlingham", "Villa Tesei", "William C. Morris"],
  },
  {
    partido: "Ituzaingó",
    barrios: ["Ituzaingó", "Villa Udaondo", "Villa Gobernador Udaondo"],
  },
  {
    partido: "Merlo",
    barrios: ["Merlo", "San Antonio de Padua", "Libertad", "Mariano Acosta", "Pontevedra", "Villa de Mayo"],
  },
  {
    partido: "Moreno",
    barrios: ["Moreno", "Paso del Rey", "Trujui", "La Reja", "Cuartel V", "Francisco Álvarez"],
  },
  {
    partido: "La Matanza",
    barrios: [
      "San Justo",
      "Ramos Mejía",
      "Lomas del Mirador",
      "Gregorio de Laferrere",
      "Villa Luzuriaga",
      "Isidro Casanova",
      "Villa Fiorito",
      "Tapiales",
      "Rafael Castillo",
      "Virrey del Pino",
      "Villa Insuperable",
      "20 de Junio",
      "González Catán",
    ],
  },
  {
    partido: "San Miguel",
    barrios: ["San Miguel", "Muñiz", "Bella Vista", "Villa Zagala"],
  },
  {
    partido: "Malvinas Argentinas",
    barrios: ["Los Polvorines", "Grand Bourg", "Tortuguitas", "Ing. Pablo Nogués", "Ingeniero Pablo Nogués"],
  },
  {
    partido: "José C. Paz",
    barrios: ["José C. Paz", "Kilómetro 42", "Km 42", "Villa Fátima"],
  },
  {
    partido: "Tres de Febrero",
    barrios: [
      "Ciudadela",
      "Caseros",
      "Ciudad Jardín",
      "Loma Hermosa",
      "Martín Coronado",
      "Santos Lugares",
      "Villa Bosch",
      "Villa Raffo",
      "Villa Saavedra",
      "11 de Septiembre",
    ],
  },
  {
    partido: "San Martín",
    barrios: [
      "San Martín",
      "Villa Ballester",
      "Villa Lynch",
      "Villa Maipú",
      "Chilavert",
      "José León Suárez",
      "Villa Libertad",
    ],
  },
  // —— Sur ——
  {
    partido: "Avellaneda",
    barrios: ["Avellaneda", "Wilde", "Sarandí", "Dock Sud", "Crucecita"],
  },
  {
    partido: "Lanús",
    barrios: [
      "Lanús",
      "Lanús Este",
      "Lanús Oeste",
      "Remedios de Escalada",
      "Valentín Alsina",
      "Monte Chingolo",
      "Gerli",
    ],
  },
  {
    partido: "Lomas de Zamora",
    barrios: [
      "Lomas de Zamora",
      "Banfield",
      "Temperley",
      "Turdera",
      "Villa Centenario",
      "Ingeniero Budge",
    ],
  },
  {
    partido: "Quilmes",
    barrios: [
      "Quilmes",
      "Quilmes Oeste",
      "Bernal",
      "Bernal Oeste",
      "San Francisco Solano",
      "Ezpeleta",
      "Villa La Florida",
    ],
  },
  {
    partido: "Berazategui",
    barrios: ["Berazategui", "Hudson", "Ranelagh", "Sourigues", "Plátanos", "Villa España"],
  },
  {
    partido: "Florencio Varela",
    barrios: ["Florencio Varela", "Bosques", "Villa Brown", "Zeballos"],
  },
  {
    partido: "Almirante Brown",
    barrios: [
      "Adrogué",
      "Burzaco",
      "Claypole",
      "Glew",
      "Longchamps",
      "Ministro Rivadavia",
      "San José",
    ],
  },
  {
    partido: "Esteban Echeverría",
    barrios: ["Monte Grande", "El Jagüel", "Luis Guillón", "9 de Abril"],
  },
  {
    partido: "Ezeiza",
    barrios: ["Ezeiza", "Canning", "La Unión", "Tristán Suárez", "Carlos Spegazzini"],
  },
  {
    partido: "Presidente Perón",
    barrios: ["Guernica", "Villa Santo Domingo"],
  },
  // —— La Plata y corredor sur ——
  {
    partido: "La Plata",
    barrios: [
      "La Plata",
      "La Plata Centro",
      "City Bell",
      "Gonnet",
      "Tolosa",
      "Ringuelet",
      "Los Hornos",
      "Villa Elisa",
    ],
  },
  { partido: "Ensenada", barrios: ["Ensenada", "Punta Lara"] },
  { partido: "Berisso", barrios: ["Berisso", "Villa Zula"] },
] as const;

/** Aliases GBA + casos especiales CABA (PoP carrier). Barrios CABA estándar se generan en buildCityNeighborhoodAliases(). */
export const GBA_CITY_NEIGHBORHOOD_ALIASES: ReadonlyArray<{
  city: string;
  neighborhood: string;
  canonicalCity: string;
}> = [
  // CABA — PoP Telecom / carrier quirks (tráfico real en geo_traffic_stats)
  { city: "Avellaneda", neighborhood: "Barracas", canonicalCity: "Buenos Aires" },
  { city: "Liniers", neighborhood: "Mataderos", canonicalCity: "Buenos Aires" },
  { city: "Buenos Aires", neighborhood: "Microcentro", canonicalCity: "Buenos Aires" },
  { city: "Buenos Aires", neighborhood: "Centro", canonicalCity: "Buenos Aires" },
  // GBA norte
  { city: "Olivos", neighborhood: "Vicente López", canonicalCity: "Vicente López" },
  { city: "Florida", neighborhood: "Vicente López", canonicalCity: "Vicente López" },
  { city: "Munro", neighborhood: "Vicente López", canonicalCity: "Vicente López" },
  { city: "Martínez", neighborhood: "San Isidro", canonicalCity: "San Isidro" },
  { city: "Beccar", neighborhood: "San Isidro", canonicalCity: "San Isidro" },
  { city: "Acassuso", neighborhood: "San Isidro", canonicalCity: "San Isidro" },
  { city: "Boulogne", neighborhood: "San Isidro", canonicalCity: "San Isidro" },
  { city: "General Pacheco", neighborhood: "Tigre", canonicalCity: "Tigre" },
  { city: "Don Torcuato", neighborhood: "Tigre", canonicalCity: "Tigre" },
  { city: "Benavídez", neighborhood: "Tigre", canonicalCity: "Tigre" },
  { city: "Garín", neighborhood: "Escobar", canonicalCity: "Escobar" },
  { city: "Belén de Escobar", neighborhood: "Escobar", canonicalCity: "Escobar" },
  { city: "Del Viso", neighborhood: "Pilar", canonicalCity: "Pilar" },
  // GBA oeste
  { city: "Castelar", neighborhood: "Morón", canonicalCity: "Morón" },
  { city: "Haedo", neighborhood: "Morón", canonicalCity: "Morón" },
  { city: "El Palomar", neighborhood: "Morón", canonicalCity: "Morón" },
  // CF reporta Villa Sarmiento + Tres de Febrero (geo real: Morón) — mantener par CF
  { city: "Villa Sarmiento", neighborhood: "Tres de Febrero", canonicalCity: "Morón" },
  // CF reporta Ituzaingó + Morón (geo real: partido Ituzaingó) — mantener par CF
  { city: "Ituzaingó", neighborhood: "Morón", canonicalCity: "Ituzaingó" },
  { city: "Caseros", neighborhood: "Tres de Febrero", canonicalCity: "Tres de Febrero" },
  { city: "Ciudad Jardín", neighborhood: "Tres de Febrero", canonicalCity: "Tres de Febrero" },
  { city: "Ciudadela", neighborhood: "Tres de Febrero", canonicalCity: "Tres de Febrero" },
  { city: "San Justo", neighborhood: "La Matanza", canonicalCity: "La Matanza" },
  { city: "Ramos Mejía", neighborhood: "La Matanza", canonicalCity: "La Matanza" },
  { city: "Lomas del Mirador", neighborhood: "La Matanza", canonicalCity: "La Matanza" },
  { city: "Gregorio de Laferrere", neighborhood: "La Matanza", canonicalCity: "La Matanza" },
  { city: "Isidro Casanova", neighborhood: "La Matanza", canonicalCity: "La Matanza" },
  { city: "Villa Ballester", neighborhood: "San Martín", canonicalCity: "San Martín" },
  { city: "Los Polvorines", neighborhood: "Malvinas Argentinas", canonicalCity: "Malvinas Argentinas" },
  { city: "Grand Bourg", neighborhood: "Malvinas Argentinas", canonicalCity: "Malvinas Argentinas" },
  { city: "Km 42", neighborhood: "José C. Paz", canonicalCity: "José C. Paz" },
  { city: "Kilómetro 42", neighborhood: "José C. Paz", canonicalCity: "José C. Paz" },
  // GBA sur
  { city: "Wilde", neighborhood: "Avellaneda", canonicalCity: "Avellaneda" },
  { city: "Sarandí", neighborhood: "Avellaneda", canonicalCity: "Avellaneda" },
  { city: "Dock Sud", neighborhood: "Avellaneda", canonicalCity: "Avellaneda" },
  { city: "Remedios de Escalada", neighborhood: "Lanús", canonicalCity: "Lanús" },
  { city: "Valentín Alsina", neighborhood: "Lanús", canonicalCity: "Lanús" },
  { city: "Monte Chingolo", neighborhood: "Lanús", canonicalCity: "Lanús" },
  { city: "Gerli", neighborhood: "Lanús", canonicalCity: "Lanús" },
  { city: "Banfield", neighborhood: "Lomas de Zamora", canonicalCity: "Lomas de Zamora" },
  { city: "Temperley", neighborhood: "Lomas de Zamora", canonicalCity: "Lomas de Zamora" },
  { city: "Bernal", neighborhood: "Quilmes", canonicalCity: "Quilmes" },
  { city: "Ezpeleta", neighborhood: "Quilmes", canonicalCity: "Quilmes" },
  { city: "San Francisco Solano", neighborhood: "Quilmes", canonicalCity: "Quilmes" },
  { city: "Adrogué", neighborhood: "Almirante Brown", canonicalCity: "Almirante Brown" },
  { city: "Burzaco", neighborhood: "Almirante Brown", canonicalCity: "Almirante Brown" },
  { city: "Monte Grande", neighborhood: "Esteban Echeverría", canonicalCity: "Esteban Echeverría" },
  { city: "El Jagüel", neighborhood: "Esteban Echeverría", canonicalCity: "Esteban Echeverría" },
  { city: "Canning", neighborhood: "Ezeiza", canonicalCity: "Ezeiza" },
  // La Plata
  { city: "Berisso", neighborhood: "La Plata Centro", canonicalCity: "La Plata" },
  { city: "City Bell", neighborhood: "La Plata", canonicalCity: "La Plata" },
  { city: "Gonnet", neighborhood: "La Plata", canonicalCity: "La Plata" },
  { city: "Tolosa", neighborhood: "La Plata", canonicalCity: "La Plata" },
  { city: "Ringuelet", neighborhood: "La Plata", canonicalCity: "La Plata" },
  { city: "Los Hornos", neighborhood: "La Plata", canonicalCity: "La Plata" },
];

/** Lista completa para resolución CF (CABA barrios auto + GBA + especiales). */
export function buildCityNeighborhoodAliases(): ReadonlyArray<{
  city: string;
  neighborhood: string;
  canonicalCity: string;
}> {
  const cabaBarrioAliases = CABA_BETA_NEIGHBORHOODS.map((neighborhood) => ({
    city: "Buenos Aires",
    neighborhood,
    canonicalCity: "Buenos Aires",
  }));
  return [...GBA_CITY_NEIGHBORHOOD_ALIASES, ...cabaBarrioAliases];
}

/** @deprecated Usar buildCityNeighborhoodAliases() — export legacy para imports existentes. */
export const CITY_NEIGHBORHOOD_ALIASES = buildCityNeighborhoodAliases();

export interface NeighborhoodBetaTarget {
  city: string;
  neighborhood: string;
}

function sortTargets(a: NeighborhoodBetaTarget, b: NeighborhoodBetaTarget): number {
  return `${a.city} ${a.neighborhood}`.localeCompare(`${b.city} ${b.neighborhood}`, "es");
}

/** Pares ciudad+barrio para expansión CABA (48 GCBA + carrier PoP). */
export function buildCabaBetaTargets(): NeighborhoodBetaTarget[] {
  const targets: NeighborhoodBetaTarget[] = CABA_BETA_NEIGHBORHOODS.map((neighborhood) => ({
    city: "Buenos Aires",
    neighborhood,
  }));
  for (const n of CABA_CARRIER_NEIGHBORHOODS) {
    targets.push({ city: "Buenos Aires", neighborhood: n });
  }
  targets.push({ city: "Avellaneda", neighborhood: "Barracas" });
  return targets;
}

/** Pares partido+localidad GBA, aliases CF y localidad-como-ciudad. */
export function buildGbaBetaTargets(): NeighborhoodBetaTarget[] {
  const targets = new Map<string, NeighborhoodBetaTarget>();
  const add = (city: string, neighborhood: string) => {
    const key = `${normGeoKey(city)}::${normGeoKey(neighborhood)}`;
    if (!targets.has(key)) targets.set(key, { city, neighborhood });
  };

  for (const cluster of GBA_BETA_CLUSTERS) {
    for (const barrio of cluster.barrios) {
      add(cluster.partido, barrio);
      if (normGeoKey(barrio) !== normGeoKey(cluster.partido)) {
        add(barrio, barrio);
      }
    }
  }

  for (const alias of GBA_CITY_NEIGHBORHOOD_ALIASES) {
    if (normGeoKey(alias.canonicalCity) === normGeoKey("Buenos Aires")) continue;
    add(alias.city, alias.neighborhood);
    if (alias.canonicalCity !== alias.city) {
      add(alias.canonicalCity, alias.neighborhood);
    }
  }

  return Array.from(targets.values()).sort(sortTargets);
}

/** Catálogo unificado para admin (CABA + GBA sin duplicar). */
export function buildNeighborhoodSeedCatalog(): NeighborhoodBetaTarget[] {
  const targets = new Map<string, NeighborhoodBetaTarget>();
  const add = (city: string, neighborhood: string) => {
    const key = `${normGeoKey(city)}::${normGeoKey(neighborhood)}`;
    if (!targets.has(key)) targets.set(key, { city, neighborhood });
  };
  for (const t of buildCabaBetaTargets()) add(t.city, t.neighborhood);
  for (const t of buildGbaBetaTargets()) add(t.city, t.neighborhood);
  return Array.from(targets.values()).sort(sortTargets);
}

export function cabaBetaTargetCount(): number {
  return buildCabaBetaTargets().length;
}

export function gbaBetaTargetCount(): number {
  return buildGbaBetaTargets().length;
}

export function isCabaBetaNeighborhood(neighborhood: string | null | undefined): boolean {
  if (!neighborhood?.trim()) return false;
  return CABA_BETA_NEIGHBORHOOD_SET.has(normGeoKey(neighborhood));
}

export function formatNeighborhoodBetaLabel(
  neighborhood: string | null | undefined,
  city: string | null | undefined,
): string | null {
  if (!neighborhood?.trim()) return null;
  const n = neighborhood.trim();
  if (isCabaBetaNeighborhood(n)) return `Barrio ${n} · CABA`;
  if (city?.trim()) return `Barrio ${n} · ${city.trim()}`;
  return `Barrio ${n}`;
}

/** Valida integridad del catálogo (usar en CI o pre-deploy). */
export function validateNeighborhoodBetaCatalog(): string[] {
  const errors: string[] = [];
  const GCBA_COUNT = 48;
  if (CABA_BETA_NEIGHBORHOODS.length !== GCBA_COUNT) {
    errors.push(`CABA: expected ${GCBA_COUNT} barrios, got ${CABA_BETA_NEIGHBORHOODS.length}`);
  }
  const conurbano = [
    "Almirante Brown", "Avellaneda", "Berazategui", "Esteban Echeverría", "Ezeiza",
    "Florencio Varela", "Hurlingham", "Ituzaingó", "José C. Paz", "La Matanza", "Lanús",
    "Lomas de Zamora", "Malvinas Argentinas", "Merlo", "Moreno", "Morón", "Presidente Perón",
    "Quilmes", "San Fernando", "San Isidro", "San Miguel", "San Martín", "Tigre",
    "Tres de Febrero", "Vicente López",
  ];
  const partidos = new Set(GBA_BETA_CLUSTERS.map((c) => normGeoKey(c.partido)));
  for (const p of conurbano) {
    if (!partidos.has(normGeoKey(p))) errors.push(`GBA: missing conurbano partido ${p}`);
  }
  if (!partidos.has(normGeoKey("Ciudadela")) && !GBA_BETA_CLUSTERS.some(
    (c) => normGeoKey(c.partido) === normGeoKey("Tres de Febrero") &&
      c.barrios.some((b) => normGeoKey(b) === normGeoKey("Ciudadela")),
  )) {
    errors.push("GBA: Ciudadela must be under Tres de Febrero");
  }
  if (GBA_BETA_CLUSTERS.some(
    (c) => normGeoKey(c.partido) === normGeoKey("La Matanza") &&
      c.barrios.some((b) => normGeoKey(b) === normGeoKey("Ciudadela")),
  )) {
    errors.push("GBA: Ciudadela must not be under La Matanza");
  }
  for (const c of GBA_BETA_CLUSTERS) {
    const seen = new Set<string>();
    for (const b of c.barrios) {
      const k = normGeoKey(b);
      if (seen.has(k)) errors.push(`GBA: duplicate ${b} in ${c.partido}`);
      seen.add(k);
    }
  }
  for (const carrier of CABA_CARRIER_NEIGHBORHOODS) {
    const seeded = buildCabaBetaTargets().some(
      (t) => normGeoKey(t.neighborhood) === normGeoKey(carrier),
    );
    if (!seeded) errors.push(`CABA: carrier zone ${carrier} not in buildCabaBetaTargets`);
  }
  return errors;
}